// Permission types
export type Permission = 'View' | 'Edit' | 'Report' | 'Sign' | 'Manage';

export interface FeaturePermissions {
  View: boolean;
  Edit: boolean;
  Report: boolean;
  Sign: boolean;
  Manage: boolean;
}

export interface TeamMemberPermissions {
  [featureName: string]: FeaturePermissions;
}

// Map tab keys to permission feature names
const TAB_TO_PERMISSION_MAP: Record<string, string> = {
  'overview': 'Overview',
  'setup': 'Setup',
  'specifications': 'Specifications',
  'material-traceability': 'MaterialTraceability',
  'equipment-and-consumable-registry': 'Registry',
  'Other Documents': 'OtherDocuments',
  'pipefitter': 'Pipefitter',
  'production': 'Production',
  'team-members': 'TeamMembers',
  'reports': 'Reports',
  'analytics': 'Analytics',
};

/**
 * Get permissions for a specific tab/feature
 */
export const getFeaturePermissions = (
  permissions: TeamMemberPermissions | undefined,
  tabKey: string
): FeaturePermissions | null => {
  if (!permissions) {
    // If no permissions, allow all (for backward compatibility)
    return {
      View: true,
      Edit: true,
      Report: true,
      Sign: true,
      Manage: true,
    };
  }

  const featureName = TAB_TO_PERMISSION_MAP[tabKey];
  if (!featureName) {
    // If tab not in map, allow all (for backward compatibility)
    return {
      View: true,
      Edit: true,
      Report: true,
      Sign: true,
      Manage: true,
    };
  }

  const featurePermissions = permissions[featureName];
  if (!featurePermissions) {
    // If feature not found, deny all
    return {
      View: false,
      Edit: false,
      Report: false,
      Sign: false,
      Manage: false,
    };
  }

  return featurePermissions;
};

/**
 * Check if user has a specific permission for a tab/feature
 */
export const hasPermission = (
  permissions: TeamMemberPermissions | undefined,
  tabKey: string,
  permission: Permission
): boolean => {
  const featurePermissions = getFeaturePermissions(permissions, tabKey);
  if (!featurePermissions) {
    return false;
  }
  return featurePermissions[permission] === true;
};

/**
 * Check if user can view a tab
 * @param permissions - Team member permissions from project
 * @param tabKey - The tab key to check
 * @param userRole - Optional user role (super_admin, admin, user, viewer)
 */
export const canViewTab = (
  permissions: TeamMemberPermissions | undefined,
  tabKey: string,
  userRole?: string | null
): boolean => {
  // Super admin and admin get full access
  if (userRole === 'super_admin' || userRole === 'admin') {
    return true;
  }
  return hasPermission(permissions, tabKey, 'View');
};

/**
 * Check if user can edit in a tab
 * @param permissions - Team member permissions from project
 * @param tabKey - The tab key to check
 * @param userRole - Optional user role (super_admin, admin, user, viewer)
 */
export const canEditTab = (
  permissions: TeamMemberPermissions | undefined,
  tabKey: string,
  userRole?: string | null
): boolean => {
  // Super admin and admin get full access
  if (userRole === 'super_admin' || userRole === 'admin') {
    return true;
  }
  return hasPermission(permissions, tabKey, 'Edit');
};

/**
 * Check if user can generate reports in a tab
 * @param permissions - Team member permissions from project
 * @param tabKey - The tab key to check
 * @param userRole - Optional user role (super_admin, admin, user, viewer)
 */
export const canReportTab = (
  permissions: TeamMemberPermissions | undefined,
  tabKey: string,
  userRole?: string | null
): boolean => {
  // Super admin and admin get full access
  if (userRole === 'super_admin' || userRole === 'admin') {
    return true;
  }
  return hasPermission(permissions, tabKey, 'Report');
};

/**
 * Check if user can sign in a tab
 * @param permissions - Team member permissions from project
 * @param tabKey - The tab key to check
 * @param userRole - Optional user role (super_admin, admin, user, viewer)
 */
export const canSignTab = (
  permissions: TeamMemberPermissions | undefined,
  tabKey: string,
  userRole?: string | null
): boolean => {
  // Super admin and admin get full access
  if (userRole === 'super_admin' || userRole === 'admin') {
    return true;
  }
  return hasPermission(permissions, tabKey, 'Sign');
};

